import { useAppLocalization } from "../../../../../../localization/components/AppLocalizationProvider"
import React, { useMemo } from "react"
import { useApiQuery } from "../../../../../../../core/api/useApiQuery"
import { useApiQueryTable } from "../../../../../../../core/api/components/ApiQueryTable"
import { MRT_ColumnDef } from "material-react-table"
import { AdyenAccountHoldersResponse, getAdyenAccountHolders, SupportAdyenQuery } from "../../adyenApi"
import { AdyenAccountHolder, AdyenAccountHolderAgreementStatus } from "../../adyenTypes"
import { AccountHolderDetails } from "./AccountHolderDetails"
import { AgreementStatusChip } from "./AgreementStatusChip"
import { StandardTwoLinesCell } from "../../../../../../../shared/components/table/StandardTwoLinesCell"
import { Stack, Typography } from "@mui/material"
import { StandardChip } from "../../../../../../../shared/components/badges/StandardChip"
import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"

function transformData(data: AdyenAccountHoldersResponse) {
    return data.accountHolders
}

export function AccountHolders() {
    const { l10n } = useAppLocalization()

    const accountHolders = useApiQuery(getAdyenAccountHolders, {
        queryName: SupportAdyenQuery.ACCOUNT_HOLDERS,
        dependencies: {},
        select: transformData,
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(accountHolders)

    const columns = useMemo<MRT_ColumnDef<AdyenAccountHolder>[]>(
        () => [
            columnFactory.nameColumn({
                header: l10n.getString("adyen-account-holders-table-header-id"),
                accessorKey: "id",
                Cell: ({ row }) => (
                    <StandardTwoLinesCell
                        title={
                            <Stack direction="row" alignItems="center" spacing=".5rem">
                                <Typography variant="body2">{row.original.id}</Typography>
                                {row.original.isLiable && (
                                    <LocalizedStrict id="adyen-account-holders-table-cell-entity-liable">
                                        <StandardChip variant="neutral" placement="table">
                                            Liable
                                        </StandardChip>
                                    </LocalizedStrict>
                                )}
                            </Stack>
                        }
                        subtitle1={
                            <LocalizedStrict
                                id="adyen-account-holders-table-cell-entity-id"
                                vars={{ entityId: row.original.legalEntity.id }}
                            >
                                <span>Legal entity ID: {row.original.legalEntity.id}</span>
                            </LocalizedStrict>
                        }
                    />
                ),
            }),
            columnFactory.shortNameColumn({
                header: l10n.getString("adyen-account-holders-table-header-supplier"),
                accessorKey: "supplier.name",
            }),
            columnFactory.statusColumn({
                header: l10n.getString("adyen-account-holders-table-header-status"),
                accessorKey: "agreement.status",
                Cell: ({ row }) => {
                    return <AgreementStatusChip placement="table" status={row.original?.agreement.status} />
                },
                filterSelectOptions: [
                    {
                        value: AdyenAccountHolderAgreementStatus.VALID,
                        text: l10n.getString("adyen-account-holders-table-header-status-valid"),
                    },
                    {
                        value: AdyenAccountHolderAgreementStatus.INVALID,
                        text: l10n.getString("adyen-account-holders-table-header-status-invalid"),
                    },
                    {
                        value: AdyenAccountHolderAgreementStatus.PENDING,
                        text: l10n.getString("adyen-account-holders-table-header-status-pending"),
                    },
                ],
            }),
            columnFactory.datetimeColumn(
                {
                    header: l10n.getString("adyen-account-holders-table-header-updated"),
                    accessorKey: "agreement.updated",
                },
                (row) => row.agreement.updated,
            ),
        ],
        [l10n, columnFactory],
    )

    return (
        <ApiQueryTable
            columns={columns}
            initialState={{ density: "spacious" }}
            renderDetailPanel={({ row }) => <AccountHolderDetails row={row} />}
            enableRowActions={false}
        />
    )
}
